import { type ProfileDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

const PROVIDER_TOPIC_PREFIX = 'web/providers/'

/**
 * Whether the provider is licensed for asynchronous (online MOUD) visits at all —
 * true when any of their licenses carries a visit type flagged `IsAsynchronous`.
 * Unlike {@link getAsyncVisitTopics}, this is not state-scoped and does not require
 * a `StateCode`, so it answers "should this provider see async features?" (e.g. the
 * dashboard Online-visits tab). Returns false for a missing profile.
 */
export const isAsyncProvider = (profile: Partial<ProfileDoctorApiOutContract> | null | undefined): boolean =>
  (profile?.Licenses ?? []).some((license) =>
    (license?.VisitTypes ?? []).some((visitType) => visitType?.IsAsynchronous),
  )

/**
 * MQTT topics an async-enabled provider should subscribe to — one per
 * `{state, async visit type}` combination the provider is licensed for:
 * `web/providers/{StateCode}-{VisitTypeId}`. The async visit type is identified by
 * its `IsAsynchronous` flag and its id is read straight from the license data, so
 * no visit-type id is hard-coded. Returns [] when the provider is not set up for
 * async visits.
 */
export const getAsyncVisitTopics = (profile: Partial<ProfileDoctorApiOutContract> | null | undefined): string[] => {
  const topics = new Set<string>()
  ;(profile?.Licenses ?? []).forEach((license) => {
    const stateCode = license?.StateCode
    if (!stateCode) return
    ;(license?.VisitTypes ?? []).forEach((visitType) => {
      if (!visitType?.IsAsynchronous) return
      // State code is lowercased so the async topic matches the broker's expected
      // casing (e.g. `web/providers/ca-851de7af-...`, not `CA-...`).
      topics.add(`${PROVIDER_TOPIC_PREFIX}${stateCode.toLowerCase()}-${visitType.VisitTypeId}`)
    })
  })
  return Array.from(topics)
}
